import styled from 'styled-components'
import { NextLink } from '@/features/shared/elements/Link'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'
import { Carousel } from '@/features/shop/components/Carousel'
import { type HeadlineContainerData } from '@/features/cms/components/CMS/types'
import { useUtmUrl } from '@/features/shared/hooks/useUtmUrl'
import { routes } from '@shared/constants/routes'
import { sanitizeHTML } from '@/features/shared/utils/sanitize'
import { ReferenceRenderer } from '@/features/cms/components/CMS/ReferenceRenderer'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { Body, Display, Headline } from '@shipt/design-system-typography'
import { spacing } from '@/features/shared/theme/tokens'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'
import { screenSizes } from '@shipt/design-system-themes'
import { Column } from '@shipt/design-system-layouts'
import { getHeadingElement } from '@/features/shared/utils/getHeadingElement'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { GridLayoutContainer } from '@/features/shared/styles/layout'

const carouselConfig = {
  spaceBetween: 32,
}

/**
 * This component corresponds to the `headline_container` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/headline_container
 * - **Production** - https://cms.shipt.com/content-type/pool/headline_container
 */
export const HeadlineContainer = ({
  content_type_id,
  id,
  data: {
    variant = 'primary',
    heading,
    subheading,
    references = [],
    horizontal_wrap,
    call_to_action,
    embedded_items = [],
    seo_heading_level,
  },
}: HeadlineContainerData) => {
  const { screenSm: isMobile } = useMediaQuery()
  const callToActionUrl = useUtmUrl(call_to_action?.url || routes.SIGN_UP.url)
  const { trackCMSElementClicked, trackCMSElementViewed } = useCMSTrackingEvents()

  const handleCTAClicked = (contentText: string) => {
    trackCMSElementClicked({
      content: contentText,
      type: 'button',
    })
  }
  const ItemWrapper = isMobile && horizontal_wrap ? Carousel : Grid

  const subHeadingLevel = heading ? seo_heading_level + 1 : seo_heading_level

  const renderSubheading = () => {
    if (!subheading) return null
    return variant === 'primary' ? (
      <PrimarySubheading align="center" as={getHeadingElement(subHeadingLevel)}>
        {subheading}
      </PrimarySubheading>
    ) : (
      <SecondarySubheading size="md" as={getHeadingElement(subHeadingLevel)}>
        {subheading}
      </SecondarySubheading>
    )
  }

  try {
    return (
      <ScrollIntoView
        onInView={() =>
          trackCMSElementViewed({
            content: 'Headline Container',
            type: 'container',
          })
        }
      >
        <GridLayoutContainer>
          <Container {...getCMSWrapperProps({ content_type_id, id })}>
            {!!heading &&
              (variant === 'primary' ? (
                <PrimaryHeading
                  align="center"
                  size="sm"
                  as={getHeadingElement(seo_heading_level)}
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(heading) }}
                />
              ) : (
                <SecondaryHeading
                  as={getHeadingElement(seo_heading_level)}
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(heading) }}
                />
              ))}
            {renderSubheading()}
            {references.length > 0 && (
              <ItemWrapper {...(isMobile && carouselConfig)}>
                <ReferenceRenderer references={references} />
              </ItemWrapper>
            )}
            {embedded_items.length > 0 && (
              <Column>
                <EmbeddedLinksWrapper>
                  {embedded_items.map((item) => {
                    const { link } = item.data
                    return (
                      <Body key={link.url} as="span" size="md">
                        <NextLink href={link.url} onClick={() => handleCTAClicked(link.text)}>
                          {link.text}
                        </NextLink>
                      </Body>
                    )
                  })}
                </EmbeddedLinksWrapper>
              </Column>
            )}
            {!!call_to_action?.text && !!call_to_action.url && (
              <HIWButtonLink
                href={callToActionUrl}
                aria-label={call_to_action.text}
                onClick={() => handleCTAClicked(call_to_action.text)}
              >
                {call_to_action.text}
              </HIWButtonLink>
            )}
          </Container>
        </GridLayoutContainer>
      </ScrollIntoView>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
  align-self: center;
`

const Container = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
`

const PrimaryHeading = styled(Display)`
  margin: ${spacing(0, 'auto', 'lg')};
  width: 100%;
  max-width: 20.0625rem;

  @media ${screenSizes.tablet} {
    max-width: 32.875rem;
  }
`

const SecondaryHeading = styled(Headline)`
  margin-bottom: ${spacing('xl')};
`

const PrimarySubheading = styled(Body)`
  width: 100%;
  max-width: 31.75rem;
  margin: ${spacing(0, 'auto', '3xl')};
`

const SecondarySubheading = styled(Headline)`
  margin-bottom: ${spacing('xl')};
`

const HIWButtonLink = styled(ButtonLink)`
  max-width: 21.5rem;
  min-width: 15.75rem;
  margin: ${spacing(0, 'auto')};
  z-index: 10;
`

const EmbeddedLinksWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${spacing('lg')};
  margin: ${spacing('sm', 0)};

  @media ${screenSizes.lgMobile} {
    gap: ${spacing('xl')};
  }

  @media ${screenSizes.tablet} {
    grid-template-columns: repeat(4, 1fr);
  }

  @media ${screenSizes.smDesktop} {
    grid-template-columns: repeat(6, 1fr);
  }
`
